import React, { useState, useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { Document, Page, pdfjs } from 'react-pdf';
import { useTranslation } from 'react-i18next';
import { imageUrl } from '../helper';

/* Serve the pdf.js worker as a static asset copied from node_modules by scripts/copyPdfWorker.js
   (see package.json prestart/prebuild) — never load executable JS from a third-party CDN. Routing
   this file through webpack's own module system (e.g. `new URL(..., import.meta.url)`) wraps it in
   a require()/module interop shim that breaks when pdfjs loads it as a raw Worker script. */
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

/* `.ai` (Adobe Illustrator) files are PDF-compatible, so pdf.js renders the raw upload directly.
   Illustrator uploads arrive as postscript, illustrator or a generic octet-stream mime type, so the
   file name extension is the reliable signal and the declared mime type is only a fallback. */
const ILLUSTRATOR_TYPES = ['application/postscript', 'application/illustrator'];
const PDF_RENDERED_EXTENSIONS = ['pdf', 'ai'];
const PDF_TYPE = 'application/pdf';

/** Lower-cased extension of a file name — empty string when the name carries none. */
const getFileExtension = (fileName) => {
  if (!fileName) return '';
  const parts = fileName.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
};

function PreviewModal({ onOpen, toggle, data, moduleName }) {
  const { t } = useTranslation('common');

  function getSlugName() {
    try {
      const { hostname } = new URL(window.location.origin);
      return hostname.split('.')[0];
    } catch (error) {
      return null;
    }
  }

  const filePreview = (element) => {
    if (!element) return '';

    const baseUrl = `https://${process.env.REACT_APP_BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com`;

    if (moduleName === 'manualDocument') {
      const fullPath = `${getSlugName()}/${process.env.REACT_APP_MANUAL_DOCUMENT}${element}`;
      const pathParts = fullPath.split('/');
      const encodedParts = pathParts.map((part) => encodeURIComponent(part));
      const encodedPath = encodedParts.join('/');
      return `${baseUrl}/${encodedPath}`;
    }
    const pathParts = element.split('/');
    const encodedParts = pathParts.map((part) => encodeURIComponent(part));
    const encodedPath = encodedParts.join('/');
    return `${baseUrl}/${encodedPath}`;
  };

  const renderImage = (element) => {
    if (moduleName === 'SpecDocument' && element?.document) {
      return filePreview(element?.document);
    }

    if (moduleName === 'specTemplate' && element?.s3Key) {
      return filePreview(element?.s3Key);
    }
    if (moduleName === 'manualDocument' && element?.s3Key) {
      return filePreview(element?.s3Key);
    }

    if (element?.document?.includes('s3.eu-west-2')) {
      return element?.document || '';
    }
    return `${imageUrl}/${element}#toolbar=0`;
  };

  const [state, setState] = useState([]);
  const fileExtension = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const fileOdsExtension = 'application/vnd.oasis.opendocument.spreadsheet';
  const s3KeyPreviewModules = ['SpecDocument', 'specTemplate', 'manualDocument'];
  const pptType = 'application/vnd.ms-powerpoint';
  const pptxType = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
  const wordType = 'application/msword';
  const documentExtension = getFileExtension(data?.fileName);

  useEffect(() => {
    const supportedTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.oasis.opendocument.spreadsheet'];
    const isSupported = supportedTypes.includes(data?.mimetype) || supportedTypes.includes(data?.mimeType) || supportedTypes.includes(data?.fileType);

    if (isSupported) {
      const fetchExcelData = async () => {
        try {
          const response = await fetch(s3KeyPreviewModules.includes(moduleName) ? filePreview(data?.s3Key) : data?.document);
          const arrayBuffer = await response.arrayBuffer();

          const workbook = XLSX.read(arrayBuffer, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const excelData = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);

          setState(excelData);
        } catch (error) {
          console.error('Error fetching or parsing the file:', error);
        }
      };

      fetchExcelData();
    }
  }, [data?.document, data?.mimetype, data?.mimeType]);

  const [numPage, setNumPage] = useState(null);
  const [pdfLoadFailed, setPdfLoadFailed] = useState(false);
  const [pdfPageWidth, setPdfPageWidth] = useState(null);
  const [isEnlarged, setIsEnlarged] = useState(false);
  const pdfContainerRef = useRef(null);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setPdfLoadFailed(false);
    setNumPage(numPages);
  };

  const onDocumentLoadError = () => {
    setPdfLoadFailed(true);
    setNumPage(null);
  };

  const declaredTypes = [data?.mimetype, data?.mimeType, data?.fileType];
  const isPdf =
    declaredTypes.some((type) => type === PDF_TYPE || ILLUSTRATOR_TYPES.includes(type)) || PDF_RENDERED_EXTENSIONS.includes(documentExtension);

  useEffect(() => {
    setPdfLoadFailed(false);
    setNumPage(null);
  }, [data?.s3Key, data?.document]);

  useEffect(() => {
    const onFsChange = () => {
      const inFs = !!document.fullscreenElement || !!document.webkitFullscreenElement;
      setIsEnlarged(inFs);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
    };
  }, []);

  useEffect(() => {
    if (!pdfContainerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      window.requestAnimationFrame(() => {
        if (entries.length) setPdfPageWidth(entries[0].contentRect.width);
      });
    });
    observer.observe(pdfContainerRef.current);
    return () => observer.disconnect();
  }, [onOpen, isPdf, isEnlarged]);

  return (
    <>
      {(data?.mimetype === fileExtension || data?.mimeType === fileExtension || data?.fileType === fileExtension || data?.fileType === fileOdsExtension) && (
        <Modal className="relative-width-modal common-custom-modal modal-medium" size="lg" show={onOpen} onHide={toggle} backdrop="static">
          <Modal.Header>
            <Modal.Title>{data?.fileName}</Modal.Title>
            <span onClick={toggle} className="icon-close cursor-pointer ml-auto" />
          </Modal.Header>
          <Modal.Body className="px-5">
            {!data && <div className="text-center">{t('PREVIEW_MODALS.NO_PREVIEW_AVAILABLE')}</div>}
            {state.length > 0 && (
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead className="thead-dark">
                    <tr>
                      {Object.keys(state[0]).map((key) => (
                        <th key={key}>{key}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {state.map((row, index) => (
                      <tr key={index}>
                        {Object.values(row).map((cell, cellIndex) => (
                          <td key={cellIndex}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Modal.Body>
        </Modal>
      )}

      {isPdf &&
        (() => {
          const pdfUrl = s3KeyPreviewModules.includes(moduleName) ? filePreview(data?.s3Key) : data?.document;

          const handleClose = () => {
            if (document.fullscreenElement) document.exitFullscreen();
            else if (document.webkitFullscreenElement) document.webkitExitFullscreen();
            setIsEnlarged(false);
            toggle();
          };

          const handleToggleEnlarge = () => {
            if (isEnlarged) {
              if (document.exitFullscreen) document.exitFullscreen();
              else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
            } else if (document.documentElement.requestFullscreen) document.documentElement.requestFullscreen();
            else if (document.documentElement.webkitRequestFullscreen) document.documentElement.webkitRequestFullscreen();
          };

          return (
            <Modal
              className={`common-custom-modal modal-medium-2x pdf-preview-modal${isEnlarged ? ' pdf-enlarged' : ''}`}
              show={onOpen}
              onHide={handleClose}
              backdrop="static"
            >
              <Modal.Header>
                <Modal.Title>{data?.fileName}</Modal.Title>
                {pdfUrl && (
                  <button
                    type="button"
                    onClick={handleToggleEnlarge}
                    aria-label="Toggle enlarged view"
                    className="btn p-0 ml-auto d-inline-flex align-items-center"
                  >
                    <i
                      className={isEnlarged ? 'icon-collapse f-20' : 'icon-expand f-24'}
                      style={isEnlarged ? { padding: '2px' } : { transform: 'rotate(45deg)' }}
                    />
                  </button>
                )}
                <span onClick={handleClose} className={`icon-close cursor-pointer ${pdfUrl ? 'ml-2' : 'ml-auto'}`} />
              </Modal.Header>
              <Modal.Body className="p-0">
                <div className="max-h-pdf p-3" ref={pdfContainerRef}>
                  {(!data || !pdfUrl || pdfLoadFailed) && <div className="text-center">{t('PREVIEW_MODALS.NO_PREVIEW_AVAILABLE')}</div>}
                  {pdfUrl && !pdfLoadFailed && (
                    <Document file={pdfUrl} onLoadSuccess={onDocumentLoadSuccess} onLoadError={onDocumentLoadError}>
                      {Array.from(new Array(numPage), (el, index) => (
                        <div key={`page_${index + 1}`} className="pdf-page-wrapper">
                          <Page pageNumber={index + 1} width={pdfPageWidth || undefined} />
                        </div>
                      ))}
                    </Document>
                  )}
                </div>
              </Modal.Body>
            </Modal>
          );
        })()}

      {(data?.mimeType === pptType || data?.mimeType === pptxType || data?.fileType === pptxType || data?.fileType === pptType) && (
        <Modal className="relative-width-modal common-custom-modal modal-medium" size="lg" show={onOpen} onHide={toggle} backdrop="static">
          <Modal.Header>
            <Modal.Title>{data?.fileName}</Modal.Title>
            <span onClick={toggle} className="icon-close cursor-pointer ml-auto" />
          </Modal.Header>
          <Modal.Body className="p-0">
            <div className="p-3">
              {!data && <div className=" text-center">{t('PREVIEW_MODALS.NO_PREVIEW_AVAILABLE')}</div>}
              <iframe
                title="ppt"
                src={`https://docs.google.com/gview?url=${s3KeyPreviewModules.includes(moduleName) ? filePreview(data?.s3Key) : data?.document}&embedded=true`}
                width="100%"
                height="100%"
                className="max-ppt-doc"
              />
            </div>
          </Modal.Body>
        </Modal>
      )}

      {(data?.mimeType === wordType || data?.fileType === wordType) && (
        <Modal className="relative-width-modal common-custom-modal modal-medium" size="lg" show={onOpen} onHide={toggle} backdrop="static">
          <Modal.Header>
            <Modal.Title>{data?.fileName}</Modal.Title>
            <span onClick={toggle} className="icon-close cursor-pointer ml-auto" />
          </Modal.Header>
          <Modal.Body className="p-0">
            <div className="p-3">
              {!data && <div className=" text-center">{t('PREVIEW_MODALS.NO_PREVIEW_AVAILABLE')}</div>}
              <iframe
                title="word"
                src={`https://view.officeapps.live.com/op/embed.aspx?src=${
                  s3KeyPreviewModules.includes(moduleName) ? filePreview(data?.s3Key) : data?.document
                }`}
                width="100%"
                height="100%"
                className="max-ppt-doc"
              />
            </div>
          </Modal.Body>
        </Modal>
      )}

      {/*
        `!isPdf` rather than three MIME comparisons: a file routed to the PDF
        viewer by its extension (`.ai`) has no PDF MIME type, so listing the
        MIME types alone let this branch render a second preview underneath it.
      */}
      {!isPdf &&
        data?.mimetype !== fileExtension &&
        data?.mimeType !== fileExtension &&
        data?.fileType !== fileExtension &&
        data?.fileType !== fileOdsExtension &&
        data?.mimeType !== pptType &&
        data?.fileType !== pptType &&
        !isPdf &&
        data?.mimeType !== wordType &&
        data?.fileType !== wordType &&
        (() => {
          const imageTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/gif'];
          const isImage = imageTypes.includes(data?.mimeType) || imageTypes.includes(data?.fileType);
          const hasFile = Boolean(data?.s3Key || data?.document);
          const canShowImage = isImage && hasFile;

          return (
            <Modal className="relative-width-modal common-custom-modal modal-medium" size="lg" show={onOpen} onHide={toggle} backdrop="static">
              <Modal.Header>
                <Modal.Title>{data?.fileName}</Modal.Title>
                <span onClick={toggle} className="icon-close cursor-pointer ml-auto" />
              </Modal.Header>
              <Modal.Body className="px-5 text-center">
                {canShowImage ? (
                  <img className="img-fluid max-h-80" alt="" src={moduleName === 'SpecDocument' ? filePreview(data?.document) : renderImage(data)} />
                ) : (
                  <div className="text-center">{t('PREVIEW_MODALS.NO_PREVIEW_AVAILABLE')}</div>
                )}
              </Modal.Body>
            </Modal>
          );
        })()}
    </>
  );
}

export default PreviewModal;
